import React, { useEffect, useState } from 'react'
import { useAuth } from '../../../context/AuthContext';
import useManageAdminKpiRecords from './useManageAdminKpiRecords';
import AdminService from '../../setting/admin/AdminService';
import AdminKpiRecordService from './AdminKpiRecordService';

const AdminKpiRecordView = () => {
  const { authState } = useAuth();
  const user = authState?.user;
  const isSuperuser = user?.role === 'superuser';
  
  const [admins, setAdmins] = useState([]);
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pointsSummary, setPointsSummary] = useState([]);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const itemsPerPage = 50;

  const {
    kpiRecords,
    isLoading,
    error,
    refreshKpiRecords
  } = useManageAdminKpiRecords();

  // Load admins for superuser filter
  useEffect(() => {
    if (isSuperuser) {
      const loadAdmins = async () => {
        try {
          const adminsRes = await AdminService.getAdmins();
          setAdmins(adminsRes.data);
        } catch (err) {
          console.error('Error loading admins:', err);
        }
      };
      loadAdmins();
    }
  }, [isSuperuser]);

  // Load records with filters
  useEffect(() => {
    const params = {};
    if (isSuperuser && selectedAdminId) {
      params.adminId = selectedAdminId;
    }
    if (filterMonth) params.month = filterMonth;
    if (filterYear) params.year = filterYear;
    refreshKpiRecords(params);
    setCurrentPage(1);
  }, [selectedAdminId, filterMonth, filterYear, isSuperuser]);

  // Load points summary
  useEffect(() => {
    const loadPointsSummary = async () => {
      setIsLoadingSummary(true);
      try {
        const params = {};
        if (filterMonth) params.month = filterMonth;
        if (filterYear) params.year = filterYear;
        const response = await AdminKpiRecordService.getAdminPointsSummary(params);
        setPointsSummary(response.data);
      } catch (err) {
        console.error('Error loading points summary:', err);
        setPointsSummary([]);
      } finally {
        setIsLoadingSummary(false);
      }
    };
    loadPointsSummary();
  }, [filterMonth, filterYear]);

  // Filter and paginate records
  const filteredRecords = kpiRecords.filter(record => {
    if (filterMonth && record.month !== parseInt(filterMonth)) return false;
    if (filterYear && record.year !== parseInt(filterYear)) return false;
    return true;
  });

  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentRecords = filteredRecords.slice(indexOfFirstItem, indexOfLastItem);

  // Generate year options (starting from 2026)
  const currentYear = new Date().getFullYear();
  const startYear = 2026;
  const yearOptions = [];
  for (let year = currentYear; year >= startYear; year--) {
    yearOptions.push(year);
  }

  useEffect(() => {
    if (error) {
      console.error('Error loading KPI records:', error);
    }
  }, [error]);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mt-4">
        <h2 className='text-5xl mb-4'>{isSuperuser ? 'Admin KPI Records' : 'My KPI Records'}</h2>
        
        {/* Points Summary */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <h3 className="text-xl font-semibold mb-3">Points Summary {filterMonth && filterYear ? `(${filterMonth}/${filterYear})` : '(Current Month)'}</h3>
          {isLoadingSummary ? (
            <p className="text-gray-500">Loading points...</p>
          ) : pointsSummary.length === 0 ? (
            <p className="text-gray-500">No points data available</p>
          ) : (
            <div className={isSuperuser ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : ""}>
              {pointsSummary.map((summary) => (
                <div 
                  key={summary.adminId} 
                  className={`p-4 border rounded-lg ${isSuperuser ? '' : 'max-w-md'}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-lg">{summary.adminName}</h4>
                    <span className={`text-2xl font-bold ${
                      summary.finalPoints >= 95 ? 'text-green-600' : 
                      summary.finalPoints >= 90 ? 'text-yellow-600' : 
                      'text-red-600'
                    }`}>
                      {summary.finalPoints.toFixed(2)}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 space-y-1">
                    <div className="flex justify-between">
                      <span>Starting Points:</span>
                      <span className="font-medium">{summary.startingPoints}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Points Deducted:</span>
                      <span className="font-medium text-red-600">-{summary.totalPointsDeducted.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t">
                      <span className="font-semibold">Final Points:</span>
                      <span className={`font-bold ${
                        summary.finalPoints >= 95 ? 'text-green-600' : 
                        summary.finalPoints >= 90 ? 'text-yellow-600' : 
                        'text-red-600'
                      }`}>
                        {summary.finalPoints.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <div className={`grid grid-cols-1 ${isSuperuser ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-4`}>
            {isSuperuser && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter by Admin
                </label>
                <select
                  value={selectedAdminId}
                  onChange={(e) => setSelectedAdminId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">All Admins</option>
                  {admins.map(admin => (
                    <option key={admin.id} value={admin.id}>
                      {admin.nickname || `Admin #${admin.id}`}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Month
              </label>
              <select
                value={filterMonth}
                onChange={(e) => setFilterMonth(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Months</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => (
                  <option key={month} value={month}>
                    {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter by Year
              </label>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Years</option>
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Records Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    No
                  </th>
                  {isSuperuser && (
                    <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </th>
                  )}
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KPI Topic
                  </th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Points
                  </th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Month/Year
                  </th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Evidence
                  </th>
                  <th scope="col" className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Notes
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan={isSuperuser ? 8 : 7} className="py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : currentRecords.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperuser ? 8 : 7} className="py-4 text-center text-gray-500">
                      No records found
                    </td>
                  </tr>
                ) : (
                  currentRecords.map((record, index) => {
                    const { id, admin, kpi, pointsDeducted, month, year, creator, evidenceImageUrl, notes } = record;
                    return (
                      <tr key={id} className="hover:bg-gray-50">
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-900">
                          {indexOfFirstItem + index + 1}
                        </td>
                        {isSuperuser && (
                          <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                            {admin?.nickname || `Admin #${admin?.id}`}
                          </td>
                        )}
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {kpi?.topic || kpi?.group || 'N/A'}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                          {pointsDeducted}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                          {month}/{year}
                        </td>
                        <td className="py-3 px-4 whitespace-nowrap text-sm text-gray-500">
                          {creator?.nickname || 'N/A'}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500">
                          {evidenceImageUrl ? (
                            <a 
                              href={evidenceImageUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-800 underline"
                            >
                              View Image
                            </a>
                          ) : (
                            <span className="text-gray-400">No image</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 max-w-xs truncate">
                          {notes || '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(indexOfLastItem, filteredRecords.length)}</span> of{' '}
                    <span className="font-medium">{filteredRecords.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {[...Array(totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 2 && page <= currentPage + 2)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === page
                                ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 3 || page === currentPage + 3) {
                        return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>;
                      }
                      return null;
                    })}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminKpiRecordView;

